import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./UI";
import { TrendingUp, Bell, FileText, ChevronDown, ChevronUp, Loader2, Share2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

export const TrendFeedCard = () => {
  const [data, setData] = useState(null);
  const [memoOpen, setMemoOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [sharing, setSharing] = useState(false);

  const load = () => api.get("/political-os/trend-feed").then(r => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const shareMemo = async () => {
    setSharing(true);
    try {
      const { data: r } = await api.post(`/political-os/entities/${data.entity_id}/portal-memo`);
      toast.success(`Memo ${r.week} is now live on the client portal`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not share memo"); }
    finally { setSharing(false); }
  };

  const savePrefs = async (patch) => {
    try { await api.put("/political-os/alert-prefs", patch); toast.success("Alert preferences saved"); load(); }
    catch { toast.error("Could not save preferences"); }
  };

  if (data === false || data === null) return null;

  return (
    <Card padded={false} data-testid="trend-feed-card">
      <div className="px-5 py-4 border-b border-white/10 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-cyan-300" /> Race trend feed
          </div>
          <div className="text-sm text-slate-300 mt-0.5">Daily web snapshots covering both sides — your race and your opponents' ads & press. Shifts are flagged and alerted.</div>
        </div>
        <div className="flex items-center gap-2" data-testid="alert-prefs">
          <Bell className="w-3.5 h-3.5 text-slate-500" />
          <select value={data.prefs?.channel || "email"} onChange={(e) => savePrefs({ channel: e.target.value })}
            data-testid="alert-channel-select"
            className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white focus:outline-none">
            <option value="email">Email alerts</option>
            <option value="sms">SMS alerts</option>
            <option value="both">Email + SMS</option>
            <option value="none">No alerts</option>
          </select>
          {["sms", "both"].includes(data.prefs?.channel) && (
            <input defaultValue={data.prefs?.phone || ""} placeholder="+1 555 000 0000" type="tel"
              onBlur={(e) => e.target.value !== (data.prefs?.phone || "") && savePrefs({ phone: e.target.value })}
              data-testid="alert-phone-input"
              className="w-32 bg-slate-900/80 border border-white/15 rounded-md px-2 py-1 text-xs text-white placeholder-slate-600 focus:outline-none" />
          )}
          <button onClick={() => savePrefs({ mute_active_entity: !data.prefs?.muted })}
            data-testid="alert-mute-btn"
            className={`text-[10px] px-2 py-1 rounded-full border ${data.prefs?.muted
              ? "bg-rose-400/10 border-rose-400/30 text-rose-300"
              : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
            {data.prefs?.muted ? "Muted — unmute" : "Mute this race"}
          </button>
          <button onClick={() => {
              if (!data.prefs?.sms_digest && !data.prefs?.phone) { toast.error("Add your phone number first (pick SMS or Email + SMS)"); return; }
              savePrefs({ sms_digest: !data.prefs?.sms_digest });
            }}
            data-testid="sms-digest-toggle" title="2-line race summary texted every morning"
            className={`text-[10px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${data.prefs?.sms_digest
              ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-300"
              : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
            <MessageSquareText className="w-3 h-3" /> {data.prefs?.sms_digest ? "AM SMS digest on" : "AM SMS digest"}
          </button>
        </div>
      </div>

      {data.memo && (
        <div className="px-5 py-3 border-b border-white/10 bg-cyan-400/[0.03]">
          <div className="flex items-center gap-2">
            <button onClick={() => setMemoOpen(o => !o)} className="flex-1 flex items-center gap-2 text-left" data-testid="weekly-memo-toggle">
              <FileText className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-xs font-semibold text-cyan-200 flex-1">Weekly strategy memo · {data.memo.week}</span>
              {memoOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
            </button>
            {data.portal_enabled && (
              <button onClick={shareMemo} disabled={sharing} data-testid="share-memo-btn"
                className="text-[10px] px-2.5 py-1 rounded-full bg-cyan-400/15 border border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/25 disabled:opacity-50 shrink-0 inline-flex items-center gap-1">
                <Share2 className="w-3 h-3" /> {sharing ? "Sharing…" : "Share to client portal"}
              </button>
            )}
          </div>
          {memoOpen && (
            <div className="mt-3 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto" data-testid="weekly-memo-body">
              {data.memo.memo}
            </div>
          )}
        </div>
      )}

      {data.entries?.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="trend-feed-empty">
          <Loader2 className="w-4 h-4 mx-auto mb-2 opacity-50" />
          No snapshots yet — the daily watcher runs every morning.
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {data.entries.map((e, i) => (
            <div key={e.id || i} className="px-5 py-3" data-testid={`trend-entry-${i}`}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-slate-500">{(e.ts || "").slice(0, 10)}</span>
                {e.shift ? (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-200" data-testid={`trend-shift-badge-${i}`}>Shift detected</span>
                ) : (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-500">Steady</span>
                )}
                <button onClick={() => setExpanded(expanded === i ? null : i)} className="ml-auto text-slate-500 hover:text-cyan-300" data-testid={`trend-expand-${i}`}>
                  {expanded === i ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
              {e.shift && e.summary && <div className="text-xs text-amber-100/90 mt-1">{e.summary}</div>}
              {expanded === i && (
                <div className="mt-2 text-xs text-slate-400 whitespace-pre-wrap leading-relaxed">
                  {e.text}
                  {(e.citations || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {e.citations.map((u, k) => (
                        <a key={k} href={u} target="_blank" rel="noreferrer"
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-200 hover:bg-fuchsia-400/20 max-w-[180px] truncate">
                          🌐 {(() => { try { return new URL(u).hostname.replace("www.", ""); } catch { return u; } })()}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
